import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useUIStore } from '@/app/store/ui';
import { useWidgetStore } from '@/app/store/widget';
import { getAgentMetadataHelpers } from '@/entities/agent';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ProjectContextPicker, useProjectContextRows } from '@/features/add-project-context';
import {
  ChatSessionLayout,
  Intro,
  SendMessageInput,
  useChatStore,
  useCurrentAgent,
} from '@/features/chats';
import { notificationService } from '@/lib/services/notification.service';
import { ROUTES } from '@/shared/config/routes';
import { backToState, readBackTo } from '@/shared/lib/back-to';
import type { ChatNavigationState, RootNavigationState } from '@/types/routing.types';
import { useToolCategories } from '@/widgets/app-shell';

// Module-level stable reference so the deps bag identity does not change per
// render. Passing it inline (`<Intro toolCategoriesDeps={{ useToolCategories }} />`)
// would build a fresh object literal on every render and toggle hook identity.
// `Intro`'s prop is REQUIRED, so together with this constant the hook the deps
// bag carries can never flip.
const INTRO_TOOL_CATEGORIES_DEPS = { useToolCategories };

// Dependency injection: the new-chat page binds the sibling `add-project-context`
// picker + rows hook so the canonical-leaf chats slice (ChatInput) never
// imports it directly. Module-level stable reference (same rationale as
// INTRO_TOOL_CATEGORIES_DEPS) keeps the deps bag identity constant per render.
const PROJECT_CONTEXT_DEPS = {
  ProjectContextPicker,
  useProjectContextRows,
};

export const Root = () => {
  const { t } = useTranslation('chats');
  const navigate = useNavigate();
  const location = useLocation();
  const workspaceId = useCurrentWorkspaceId();

  const currentAgent = useCurrentAgent();

  // Get navigation state (agentId from dashboard, openAgentsPanel from header)
  const navigationState = location.state as RootNavigationState | null;
  const targetAgentId = navigationState?.agentId;
  const shouldOpenAgentsPanel = navigationState?.openAgentsPanel;
  const hasWidgetData = Boolean(navigationState?.widgetData);
  const backTo = readBackTo(location.state);

  // Track if we're ready for auto-greeting (after reset and initialization)
  const isReadyForAutoGreeting = useRef(false);
  const hasInitialized = useRef(false);

  // Reset chat store to clean state when Root mounts, then initialize agent
  useEffect(() => {
    let isCancelled = false;
    isReadyForAutoGreeting.current = false;
    hasInitialized.current = false;

    const { reset, setAgentById } = useChatStore.getState();
    reset();
    useWidgetStore.getState().clearWidget();

    // Initialize agent after reset
    if (!workspaceId) return;

    if (targetAgentId) {
      void setAgentById(targetAgentId).then(() => {
        // To prevent race conditions when targetAgentId changes rapidly
        if (!isCancelled) {
          isReadyForAutoGreeting.current = true;
          hasInitialized.current = true;
        }
      });
    } else {
      // No agent selected - redirect to dashboard
      void navigate(ROUTES.HOME, { replace: true });
    }

    return () => {
      isCancelled = true;
    };
  }, [workspaceId, targetAgentId, navigate]);

  // Handle widget data override with minimal dependencies - only runs on actual navigation
  useEffect(() => {
    // Read widgetData fresh from location.state to avoid stale closures
    const navState = location.state as RootNavigationState | null;
    const widgetData = navState?.widgetData;
    const agentId = navState?.agentId;

    if (widgetData && agentId) {
      useWidgetStore.getState().setWidget({
        ...widgetData,
        agentName: agentId,
      });
      useUIStore.getState().openSidePanel('widget', widgetData.fullWidth ? 'fullscreen' : 'split');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally use location.key to trigger on navigation, state is read fresh inside
  }, [location.key]);

  // Handle sidepanel when navigating to Root
  useEffect(() => {
    const { openSidePanel, closeSidePanel } = useUIStore.getState();
    if (shouldOpenAgentsPanel) {
      openSidePanel('agents');
    } else if (!hasWidgetData) {
      closeSidePanel();
    }
  }, [shouldOpenAgentsPanel, hasWidgetData]);

  const createNewChat = useCallback(
    async (input: SendMessageInput): Promise<void> => {
      try {
        if (!currentAgent) {
          throw new Error('No current agent selected.');
        }

        const { initializeChat } = useChatStore.getState();
        const newChatId = await initializeChat();

        // Check if stream mode is enabled via global flag
        const targetRoute = ROUTES.CHAT_SESSION(newChatId);

        void navigate(targetRoute, {
          state: {
            agentId: currentAgent.id,
            args: input,
            ...(backTo && backToState(backTo)),
          } satisfies ChatNavigationState,
          replace: true,
        });
      } catch {
        notificationService.showError(t('root.failedToCreateANew'));
      }
    },
    [currentAgent, navigate, t, backTo],
  );

  // Handle auto-greeting: automatically create chat and send greeting for agents that have it configured
  useEffect(() => {
    // Only run auto-greeting if we're ready (after proper initialization)
    if (currentAgent == null || !isReadyForAutoGreeting.current) return;

    const autoGreeting = getAgentMetadataHelpers().getAgentAutoGreeting(currentAgent.id);

    if (autoGreeting) {
      isReadyForAutoGreeting.current = false; // Prevent re-triggering
      void createNewChat({
        agentId: currentAgent.id,
        prompt: autoGreeting,
        isHidden: true,
      });
    }
  }, [currentAgent, createNewChat]);

  return (
    <ChatSessionLayout isNewChat onSend={createNewChat} projectContextDeps={PROJECT_CONTEXT_DEPS}>
      <Intro toolCategoriesDeps={INTRO_TOOL_CATEGORIES_DEPS} />
    </ChatSessionLayout>
  );
};

// Root uses global chat store via useChatStore
